import { Injectable } from '@angular/core';
import { firstValueFrom, Observable, of } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { BillingService, RevioCustomer } from './billing.service';
import { DomainMappingService } from './domain-mapping.service';
import { StorageService } from './storage.service';

export type ResolutionStrategy =
  | 'domain-mapping'
  | 'domain'
  | 'name'
  | 'name-partial'
  | 'name-contains'
  | 'name-first'
  | 'cached';

export interface CustomerResolutionResult {
  customer: RevioCustomer;
  strategy: ResolutionStrategy;
  suggestions?: RevioCustomer[];
}

type CustomerMatchType = 'exact' | 'startsWith' | 'contains' | 'first';

interface CustomerMatch {
  customer: RevioCustomer;
  matchType: CustomerMatchType;
}

interface MatchStrategyResult {
  strategy: ResolutionStrategy;
  isExactMatch: boolean;
}

@Injectable({ providedIn: 'root' })
export class RevioCustomerResolverService {
  private static readonly MIN_CUSTOMER_ID = 1;

  constructor(
    private readonly billingService: BillingService,
    private readonly domainMappingService: DomainMappingService,
    private readonly storageService: StorageService
  ) {}

  async resolveCustomerByDomain(
    domain: string,
    searchTerm?: string
  ): Promise<CustomerResolutionResult | null> {
    if (!this.isValidDomain(domain)) {
      return null;
    }

    const resolutionStrategies = this.buildResolutionStrategies(domain, searchTerm);

    for (const strategy of resolutionStrategies) {
      const result = await strategy();
      if (result) {
        return result;
      }
    }

    return null;
  }

  getCachedCustomerId(domain: string): number | null {
    return this.storageService.getRevioParentCustomerId(domain);
  }

  cacheCustomerId(domain: string, customerId: number): void {
    this.storageService.setRevioParentCustomerId(domain, customerId);
  }

  private isValidDomain(domain: string): boolean {
    return !!domain?.trim();
  }

  private buildResolutionStrategies(
    domain: string,
    searchTerm?: string
  ): Array<() => Promise<CustomerResolutionResult | null>> {
    const strategies: Array<() => Promise<CustomerResolutionResult | null>> = [
      () => this.tryDomainMapping(domain),
      () => this.tryDomainSearch(domain),
    ];

    if (this.shouldTryNameSearch(searchTerm, domain)) {
      strategies.push(() => this.tryNameSearch(searchTerm!, domain));
    }

    strategies.push(() => this.tryCachedCustomer(domain));

    return strategies;
  }

  private shouldTryNameSearch(searchTerm?: string, domain?: string): boolean {
    return !!searchTerm && searchTerm !== domain;
  }

  private async tryDomainMapping(
    domain: string
  ): Promise<CustomerResolutionResult | null> {
    const domainMapping = await this.safeGetDomainMapping(domain);
    if (!domainMapping?.customerId) {
      return null;
    }

    const customers = await this.safeGetCustomerById(domainMapping.customerId);
    if (!this.hasValidCustomers(customers)) {
      return null;
    }

    return {
      customer: customers[0],
      strategy: 'domain-mapping',
    };
  }

  private async tryDomainSearch(
    domain: string
  ): Promise<CustomerResolutionResult | null> {
    const customers = await this.safeSearchCustomersByDomain(domain);
    if (!this.hasValidCustomers(customers)) {
      return null;
    }

    return {
      customer: customers[0],
      strategy: 'domain',
      suggestions: this.buildSuggestions(customers),
    };
  }

  private async tryNameSearch(
    searchTerm: string,
    domain: string
  ): Promise<CustomerResolutionResult | null> {
    const customers = await this.safeSearchCustomersByName(searchTerm);
    if (!this.hasValidCustomers(customers)) {
      return null;
    }

    const match = this.findBestCustomerMatch(customers, searchTerm, domain);
    if (!match) {
      return null;
    }

    const strategyResult = this.determineMatchStrategy(match);

    return {
      customer: match.customer,
      strategy: strategyResult.strategy,
      suggestions: this.buildSuggestionsForNameSearch(
        customers,
        strategyResult.isExactMatch
      ),
    };
  }

  private async tryCachedCustomer(
    domain: string
  ): Promise<CustomerResolutionResult | null> {
    const cachedId = this.storageService.getRevioParentCustomerId(domain);
    if (!cachedId) {
      return null;
    }

    const customers = await this.safeGetCustomerById(cachedId);
    if (!this.hasValidCustomers(customers)) {
      return null;
    }

    return {
      customer: customers[0],
      strategy: 'cached',
    };
  }

  private async safeGetDomainMapping(
    domain: string
  ): Promise<import('../components/settings/domain-mapping-settings.types').DomainCustomerMapping | null> {
    try {
      return await firstValueFrom(
        this.domainMappingService.getByDomain(domain).pipe(
          catchError(() => of(null))
        )
      );
    } catch {
      return null;
    }
  }

  private async safeGetCustomerById(
    customerId: number
  ): Promise<RevioCustomer[]> {
    try {
      return await firstValueFrom(
        this.billingService.getRevioCustomerById(customerId).pipe(
          catchError(() => of([]))
        )
      );
    } catch {
      return [];
    }
  }

  private async safeSearchCustomersByDomain(
    domain: string
  ): Promise<RevioCustomer[]> {
    try {
      return await firstValueFrom(
        this.billingService.revioSearchCustomersByDomain(domain).pipe(
          catchError(() => of([]))
        )
      );
    } catch {
      return [];
    }
  }

  private async safeSearchCustomersByName(
    name: string
  ): Promise<RevioCustomer[]> {
    try {
      return await firstValueFrom(
        this.billingService.revioSearchCustomersByName(name).pipe(
          catchError(() => of([]))
        )
      );
    } catch {
      return [];
    }
  }

  private hasValidCustomers(customers: RevioCustomer[]): boolean {
    return Array.isArray(customers) && customers.length > 0;
  }

  private buildSuggestions(customers: RevioCustomer[]): RevioCustomer[] | undefined {
    return customers.length > 1 ? customers : undefined;
  }

  private buildSuggestionsForNameSearch(
    customers: RevioCustomer[],
    isExactMatch: boolean
  ): RevioCustomer[] | undefined {
    return customers.length > 1 && !isExactMatch ? customers : undefined;
  }

  private findBestCustomerMatch(
    customers: RevioCustomer[],
    searchTerm: string,
    domain: string
  ): CustomerMatch | null {
    if (!this.hasValidCustomers(customers)) {
      return null;
    }

    const normalizedTerm = this.normalizeString(searchTerm);
    const normalizedDomain = this.normalizeString(domain);

    const exactMatch = this.findExactMatch(customers, normalizedTerm, normalizedDomain);
    if (exactMatch) {
      return { customer: exactMatch, matchType: 'exact' };
    }

    const startsWithMatch = this.findStartsWithMatch(customers, normalizedDomain);
    if (startsWithMatch) {
      return { customer: startsWithMatch, matchType: 'startsWith' };
    }

    const containsMatch = this.findContainsMatch(customers, normalizedDomain);
    if (containsMatch) {
      return { customer: containsMatch, matchType: 'contains' };
    }

    return { customer: customers[0], matchType: 'first' };
  }

  private normalizeString(value: string): string {
    return value.toLowerCase().trim();
  }

  private findExactMatch(
    customers: RevioCustomer[],
    normalizedTerm: string,
    normalizedDomain: string
  ): RevioCustomer | undefined {
    return (
      customers.find(
        (c) => this.normalizeString(c.customerName || '') === normalizedTerm
      ) ||
      customers.find(
        (c) => this.normalizeString(c.customerName || '') === normalizedDomain
      )
    );
  }

  private findStartsWithMatch(
    customers: RevioCustomer[],
    normalizedDomain: string
  ): RevioCustomer | undefined {
    return customers.find((c) =>
      this.normalizeString(c.customerName || '').startsWith(normalizedDomain)
    );
  }

  private findContainsMatch(
    customers: RevioCustomer[],
    normalizedDomain: string
  ): RevioCustomer | undefined {
    return customers.find((c) =>
      this.normalizeString(c.customerName || '').includes(normalizedDomain)
    );
  }

  private determineMatchStrategy(
    match: CustomerMatch
  ): MatchStrategyResult {
    const strategyMap: Record<CustomerMatchType, ResolutionStrategy> = {
      exact: 'name',
      startsWith: 'name-partial',
      contains: 'name-contains',
      first: 'name-first',
    };

    return {
      strategy: strategyMap[match.matchType],
      isExactMatch: match.matchType === 'exact',
    };
  }


}

